import React, { useState } from "react";
import { Loader2, Printer, CheckCircle2, Banknote, CreditCard, Wallet } from "lucide-react";
import { API } from "../api";

const METHODS = [
  { id: "card", label: "Card", icon: CreditCard },
  { id: "cash", label: "Cash", icon: Banknote },
  { id: "other", label: "Other", icon: Wallet },
];
const input = "w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm";

const SaleDone = ({ result, onAgain }) => (
  <div className="text-center py-4" data-testid="sale-success">
    <CheckCircle2 className="mx-auto text-[#2ec9c9]" size={44} />
    <p className="font-semibold text-[#16243b] mt-3">Voucher sold</p>
    <p className="font-mono text-2xl tracking-[0.2em] text-[#16243b] mt-2" data-testid="sale-code">{result.voucher.code}</p>
    <p className="text-sm text-[#5a6b80] mt-1">£{result.voucher.original_amount.toFixed(2)} · {result.emailed ? "emailed to the customer" : "not emailed"}</p>
    <div className="flex gap-3 justify-center mt-5">
      <a href={`${API}/voucher/${result.voucher.code}/pdf`} target="_blank" rel="noreferrer" data-testid="sale-print-btn"
        className="gp-btn-outline rounded-xl px-4 py-2.5 text-sm font-medium inline-flex items-center gap-2"><Printer size={16} /> Print voucher</a>
      <button onClick={onAgain} data-testid="sale-again-btn" className="gp-btn-teal rounded-xl px-4 py-2.5 text-sm font-medium">Sell another</button>
    </div>
  </div>
);

export const InStoreSaleForm = ({ products = [], onSell }) => {
  const blank = { product_id: "", amount: "", payment_method: "card", buyer_name: "", buyer_email: "", recipient_name: "", message: "", send_email: true };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      setResult(await onSell({ ...f, amount: parseFloat(f.amount) || 0, origin: window.location.origin }));
    } catch (ex) { setErr(ex?.response?.data?.detail || "Sale failed"); } finally { setBusy(false); }
  };

  if (result) return <SaleDone result={result} onAgain={() => { setResult(null); setF(blank); }} />;
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="in-store-sale-form">
      <div>
        <label className="block text-sm font-medium text-[#16243b] mb-1.5">Voucher</label>
        <select value={f.product_id} onChange={set("product_id")} className={input} data-testid="sale-product-select">
          <option value="">Custom amount</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.title} — £{Number(p.price).toFixed(2)}</option>)}
        </select>
      </div>
      {!f.product_id && (
        <input type="number" step="0.01" min="1" required value={f.amount} onChange={set("amount")} placeholder="Amount (£)" className={input} data-testid="sale-amount-input" />
      )}
      <div className="grid grid-cols-3 gap-2">
        {METHODS.map((m) => (
          <button type="button" key={m.id} onClick={() => setF({ ...f, payment_method: m.id })} data-testid={`sale-method-${m.id}`}
            className={`rounded-xl border py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${f.payment_method === m.id ? "border-[#2ec9c9] bg-[#eefafa] text-[#16243b]" : "border-gray-200 text-[#5a6b80]"}`}>
            <m.icon size={16} /> {m.label}
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input value={f.buyer_name} onChange={set("buyer_name")} placeholder="Customer name (optional)" className={input} data-testid="sale-buyer-name" />
        <input type="email" value={f.buyer_email} onChange={set("buyer_email")} placeholder="Customer email (optional)" className={input} data-testid="sale-buyer-email" />
        <input value={f.recipient_name} onChange={set("recipient_name")} placeholder="Gift for (optional)" className={input} data-testid="sale-recipient-name" />
        <input value={f.message} onChange={set("message")} placeholder="Message (optional)" className={input} data-testid="sale-message" />
      </div>
      <label className="flex items-center gap-2 text-sm text-[#5a6b80]">
        <input type="checkbox" checked={f.send_email} onChange={set("send_email")} data-testid="sale-send-email" /> Email the voucher to the customer
      </label>
      {err && <p className="text-red-500 text-sm" data-testid="sale-error">{err}</p>}
      <button disabled={busy} data-testid="sale-submit-btn" className="w-full gp-btn-teal rounded-xl py-3 font-semibold flex items-center justify-center gap-2">
        {busy && <Loader2 className="animate-spin" size={18} />} Take payment & issue voucher
      </button>
      <p className="text-xs text-[#8a97a8] text-center">Take the money on your till first — this records the sale and creates the voucher.</p>
    </form>
  );
};
